import { useCallback, useState, useEffect } from 'react';
import Paper from '@mui/material/Paper';
import Box from '@mui/material/Box';
import Tab from '@mui/material/Tab';
import Tabs from '@mui/material/Tabs';
import { useTranslation } from 'react-i18next';
import { SCUIBreadCrumb } from '../../atomic/molecules';
import { SCUIContent } from '../../atomic/organisms';
import { PageUrl } from '../../types/enums/pageUrl-enums';
import { BulkExcelUploadTab, FeeManagementListTab } from './components';
import { SCUILoadingLinear } from '../../atomic/atoms';
import { CargoManagementQuery } from '../../services/query';
import { LCWFEE } from './lcwCargoFeeManagement.classes';

interface TabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
    className?: string;
}

const TabPanel = ({ children, value, index, ...other }: TabPanelProps): React.JSX.Element | null => {
    if (value !== index) return null;
    return (
        <Paper
            component="div"
            role="tabpanel"
            id={`lcw-cargo-fee-tabpanel-${index}`}
            aria-labelledby={`lcw-cargo-fee-tab-${index}`}
            sx={{
                p: '24px',
                display: 'flex',
                flex: 1,
                backgroundColor: 'grey.50',
            }}
            {...other}
        >
            {children}
        </Paper>
    );
};

interface TabConfig {
    label: string;
    Component: React.ComponentType<any>;
    tabClassName: string;
    tabPanelClassName: string;
}

interface CargoCompanyOption {
    id: number;
    name: string;
}

export const LCWCargoFeeManagement = (): React.JSX.Element => {
    const [tabValue, setTabValue] = useState<number>(0);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [companyList, setCompanyList] = useState<CargoCompanyOption[]>([]);

    const { t } = useTranslation();

    const handleChange = (_: any, newValue: number): void => {
        setTabValue(newValue);
    };

    const tabsConfig: TabConfig[] = [
        {
            label: t('lcwCargoFeeManagement.tabs.list'),
            Component: FeeManagementListTab as React.ComponentType<any>,
            tabClassName: LCWFEE.tabFeeList,
            tabPanelClassName: LCWFEE.tabPanelFeeList,
        },
        {
            label: t('lcwCargoFeeManagement.tabs.bulkUpload'),
            Component: BulkExcelUploadTab as React.ComponentType<any>,
            tabClassName: LCWFEE.tabBulkUpload,
            tabPanelClassName: LCWFEE.tabPanelBulkUpload,
        },
    ];

    const getCompanies = useCallback(() => {
        setIsLoading(true);
        CargoManagementQuery.CargoCompanyList()
            .then((res: any) => {
                const data = res?.data || [];
                const mapped: CargoCompanyOption[] = data?.map((d: any) => ({
                    id: d.id || d.shippingCompanyId,
                    name: d.name || d.shippingCompanyName,
                }));
                setCompanyList([{ id: -1, name: t('general.all') as any }, ...mapped]);
            })
            .catch(() => setCompanyList([{ id: -1, name: t('general.all') as any }]))
            .finally(() => setIsLoading(false));
    }, [t]);

    useEffect(() => {
        getCompanies();
    }, []);

    return (
        <SCUIContent>
            <Box className={LCWFEE.page}>
                <Paper
                    className={LCWFEE.shellPaper}
                    component="div"
                    sx={{
                        p: '24px',
                        paddingTop: '36px',
                        display: 'flex',
                        flexDirection: 'column',
                        width: '100%',
                    }}
                >
                    <div className={LCWFEE.breadcrumbSection}>
                        <SCUIBreadCrumb
                            title={t('lcwCargoFeeManagement.title')}
                            componentLink={PageUrl.lcwCargoFeeManagement.url}
                        />
                    </div>
                    <div className={LCWFEE.loadingSection}>
                        <SCUILoadingLinear display={isLoading} />
                    </div>
                    <div className={LCWFEE.tabsWrap}>
                        <Tabs
                            className={LCWFEE.tabsRoot}
                            sx={{ marginTop: '12px' }}
                            value={tabValue}
                            onChange={handleChange}
                            aria-label="LCW Cargo Fee Management Tabs"
                        >
                            {tabsConfig.map((tab, index) => (
                                <Tab
                                    key={index}
                                    className={tab.tabClassName}
                                    sx={{ fontSize: '14px' }}
                                    label={tab.label}
                                    id={`lcw-cargo-fee-tab-${index}`}
                                    aria-controls={`lcw-cargo-fee-tabpanel-${index}`}
                                />
                            ))}
                        </Tabs>
                    </div>
                    {tabsConfig.map((tab, index) => (
                        <TabPanel key={index} value={tabValue} index={index} className={tab.tabPanelClassName}>
                            <tab.Component setIsLoading={setIsLoading} isLoading={isLoading} companyOptions={companyList} />
                        </TabPanel>
                    ))}
                </Paper>
            </Box>
        </SCUIContent>
    );
};
